import { Box, Checkmark, Close, IconButton, StaticTable, TextInput } from "@bcc-ui/ui-components";
import classNames from "classnames";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { ValidationError } from "yup";

import { useAbortControllers } from "components/file-sharing/useAbortControllers";
import DownArrow from "components/icons/DownArrow";
import UpArrow from "components/icons/UpArrow";
import style from "components/settings/report-signature-keys/report-signature-keys.scss";
import ReportSignatureKeyRowActions from "components/settings/report-signature-keys/ReportSignatureKeyRowActions";
import DateCell from "components/table/DateCell";
import { NAME_MAX_LENGTH } from "domain/globalConstants";
import { createSignatureKeyNameSchema, ReportSignatureKey, ReportSignatureKeyResult } from "domain/reportSignatureKeys";
import { reportSignatureKeyService } from "services/report/erasure/ReportSignatureKeyService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { hasTenantCookie } from "services/tenants/tenantCookieService";
import form from "styles/form.scss";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

type SortColumn = "name" | "created";

interface SortState {
    column: SortColumn;
    ascending: boolean;
}

interface Props {
    keys: ReportSignatureKey[];
    loading: boolean;
    failureMessage?: string;
    onKeyChange: () => void;
    showResult: (result: ReportSignatureKeyResult, succeeded: boolean) => void;
}

const ReportSignatureKeysTable = (props: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const abortControllers = useAbortControllers();
    const [sort, setSort] = React.useState<SortState>({ column: "name", ascending: true });
    const [editingKeyName, setEditingKeyName] = React.useState<string | undefined>(undefined);
    const [draftName, setDraftName] = React.useState("");
    const [nameError, setNameError] = React.useState<string | undefined>(undefined);
    const [renaming, setRenaming] = React.useState(false);

    const toggleSort = (column: SortColumn) =>
        setSort((previous) =>
            previous.column === column ? { column, ascending: !previous.ascending } : { column, ascending: true }
        );

    const sortDirection = sort.ascending ? 1 : -1;
    const sortedKeys = [...props.keys].sort((a, b) => a[sort.column].localeCompare(b[sort.column]) * sortDirection);

    const startEdit = (key: ReportSignatureKey) => {
        setEditingKeyName(key.name);
        setDraftName(key.name);
        setNameError(undefined);
    };

    const cancelEdit = () => {
        abortControllers.abortAll();
        setEditingKeyName(undefined);
        setRenaming(false);
    };

    const changeDraftName = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setDraftName(value);
        if (editingKeyName == null) {
            return;
        }
        try {
            createSignatureKeyNameSchema(
                t,
                props.keys.map((key) => key.name).filter((existingName) => existingName !== editingKeyName),
                editingKeyName
            ).validateSync(value);
            setNameError(undefined);
        } catch (error) {
            setNameError(error instanceof ValidationError ? error.message : undefined);
        }
    };

    const confirmEdit = () => {
        if (editingKeyName == null) {
            return;
        }
        const currentName = editingKeyName;
        const newName = draftName.trim();
        const abortController = new AbortController();
        abortControllers.push(abortController);
        setRenaming(true);
        usageStatisticsService.sendEvent({
            category: Category.REPORT_SIGNATURE_KEY,
            action: Action.RENAME_REPORT_SIGNATURE_KEY,
        });
        reportSignatureKeyService
            .renameKey(currentName, newName, abortController)
            .then(() => {
                props.showResult(
                    {
                        title: t("ReportSignatureKeys.renameForm.success.title"),
                        message: t("ReportSignatureKeys.renameForm.success.message", { name: newName }),
                    },
                    true
                );
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    props.showResult(
                        {
                            title: t("ReportSignatureKeys.renameForm.failed.title"),
                            message: t("ReportSignatureKeys.renameForm.failed.message"),
                        },
                        false
                    );
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setRenaming(false);
                    setEditingKeyName(undefined);
                }
            });
    };

    const sortableHeader = (column: SortColumn, label: string) => {
        const suffix = sort.column === column ? (sort.ascending ? "ascending" : "descending") : "";
        return (
            <Box
                gap={"Xxs"}
                verticalAlignment={"Center"}
                className={style.sortableHeader}
                onClick={() => toggleSort(column)}
            >
                {label}
                <button
                    type={"button"}
                    className={classNames(style.tableSortButton, {
                        [style.sortNone]: suffix === "",
                        [style.sortAscending]: suffix === "ascending",
                        [style.sortDescending]: suffix === "descending",
                    })}
                >
                    <DownArrow
                        className={style.downArrow}
                        color={suffix === "descending" ? theme.sortIconActiveColor : theme.sortIconRegularColor}
                    />
                    <UpArrow
                        className={style.upArrow}
                        color={suffix === "ascending" ? theme.sortIconActiveColor : theme.sortIconRegularColor}
                    />
                </button>
            </Box>
        );
    };

    if (props.failureMessage != null) {
        return <div className={form.error}>{props.failureMessage}</div>;
    }

    const cells = sortedKeys.map((key) => {
        const isEditingThisRow = editingKeyName === key.name;

        const nameCell = isEditingThisRow ? (
            <TextInput
                name={"name"}
                value={draftName}
                onChange={changeDraftName}
                maxLength={NAME_MAX_LENGTH}
                error={nameError}
                testIds={{
                    input: testIds.navigation.settingsDialog.reportSignatureKeys.table.renameInput.itself,
                    error: testIds.navigation.settingsDialog.reportSignatureKeys.table.renameInput.errorLabel,
                }}
            />
        ) : (
            key.name
        );

        const actionsCell = isEditingThisRow ? (
            <Box gap={"Xs"}>
                <IconButton
                    onClick={confirmEdit}
                    disabled={renaming || draftName.trim() === "" || draftName.trim() === key.name || nameError != null}
                    testId={testIds.navigation.settingsDialog.reportSignatureKeys.table.confirmRenameButton}
                >
                    <Checkmark color={theme.iconFillColor} />
                </IconButton>
                <IconButton
                    onClick={cancelEdit}
                    disabled={renaming}
                    testId={testIds.navigation.settingsDialog.reportSignatureKeys.table.cancelRenameButton}
                >
                    <Close color={theme.iconFillColor} />
                </IconButton>
            </Box>
        ) : !hasTenantCookie() ? (
            <ReportSignatureKeyRowActions
                name={key.name}
                onEdit={() => startEdit(key)}
                onKeyChange={props.onKeyChange}
                showResult={props.showResult}
            />
        ) : null;

        return [nameCell, <DateCell key={`${key.name}-created`} value={key.created} />, actionsCell];
    });

    return (
        <StaticTable
            headers={[
                { value: sortableHeader("name", t("ReportSignatureKeys.table.name")) },
                { value: sortableHeader("created", t("ReportSignatureKeys.table.created")) },
                { value: "" },
            ]}
            cells={cells}
            loading={props.loading}
            emptyStateMessage={t("ReportSignatureKeys.table.emptyStateMessage")}
            testId={testIds.navigation.settingsDialog.reportSignatureKeys.table.itself}
        />
    );
};

export default ReportSignatureKeysTable;
